/**
 * CartPulse — cart icon badge that springs/pulses when count increases.
 *
 * Usage:
 *   <CartPulse count={cartCount}>
 *     <ShoppingCart size={22} />
 *   </CartPulse>
 *
 * The badge animates scale(1→1.4→1) on every count increase using spring.
 * Shows nothing when count === 0.
 */
'use client';

import { useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn } from './utils';

interface CartPulseProps {
  count: number;
  children: ReactNode;
  badgeClassName?: string;
  className?: string;
}

export function CartPulse({ count, children, badgeClassName, className }: CartPulseProps) {
  const controls = useAnimation();
  const prevCount = useRef(count);

  useEffect(() => {
    if (count > prevCount.current) {
      controls.start({
        scale: [1, 1.45, 0.9, 1.1, 1],
        transition: { duration: 0.4, ease: 'easeOut' },
      });
    }
    prevCount.current = count;
  }, [count, controls]);

  return (
    <span className={cn('relative inline-flex', className)}>
      {children}
      {count > 0 && (
        <motion.span
          animate={controls}
          className={cn(
            'absolute -top-1.5 -end-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground tabular-nums',
            badgeClassName
          )}
          aria-label={`${count} items in cart`}
        >
          {count > 99 ? '99+' : count}
        </motion.span>
      )}
    </span>
  );
}

/**
 * FloatingCartFly — animated dot that flies from a product card to the cart icon.
 *
 * Call trigger() from your add-to-cart handler, passing the source element rect
 * and the cart icon element.
 *
 * Usage:
 *   const flyRef = useRef<CartFlyHandle>(null);
 *   <FloatingCartFly ref={flyRef} />
 *   // in add handler:
 *   flyRef.current?.fly(productButtonRef.current, cartIconRef.current);
 */
import { forwardRef, useImperativeHandle, useState } from 'react';

interface FlyDot {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export interface CartFlyHandle {
  fly: (from: HTMLElement | DOMRect, to: HTMLElement | DOMRect) => void;
}

export const FloatingCartFly = forwardRef<CartFlyHandle>((_, ref) => {
  const [dots, setDots] = useState<FlyDot[]>([]);

  useImperativeHandle(ref, () => ({
    fly(from, to) {
      const fromRect = from instanceof HTMLElement ? from.getBoundingClientRect() : from;
      const toRect = to instanceof HTMLElement ? to.getBoundingClientRect() : to;
      const id = Date.now();
      setDots((prev) => [
        ...prev,
        {
          id,
          startX: fromRect.left + fromRect.width / 2,
          startY: fromRect.top + fromRect.height / 2,
          endX: toRect.left + toRect.width / 2,
          endY: toRect.top + toRect.height / 2,
        },
      ]);
      setTimeout(() => {
        setDots((prev) => prev.filter((d) => d.id !== id));
      }, 800);
    },
  }));

  return (
    <>
      {dots.map((dot) => (
        <motion.div
          key={dot.id}
          className="pointer-events-none fixed z-[9999] h-3 w-3 rounded-full bg-primary shadow-md"
          initial={{ x: dot.startX, y: dot.startY, scale: 1, opacity: 1 }}
          animate={{
            x: dot.endX,
            y: dot.endY,
            scale: 0.3,
            opacity: 0,
          }}
          transition={{
            duration: 0.65,
            ease: [0.4, 0, 0.2, 1],
          }}
          style={{ translateX: '-50%', translateY: '-50%', top: 0, left: 0 }}
        />
      ))}
    </>
  );
});
FloatingCartFly.displayName = 'FloatingCartFly';
